import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { FaUser, FaEnvelope, FaPhone, FaHammer, FaDollarSign, FaCalendar, FaComment, FaAngleDown } from 'react-icons/fa';

import axios from "axios";
import { Heading } from '../../../components';
import { FaLocationDot } from 'react-icons/fa6';

// Backend API endpoint to submit form data
const API_PATH = 'https://timbercraft-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regular expressions for input validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {

  // Initialize form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    service: '',
    address: '',
    budget: '',
    date: '',
    comment: ''
  });

  // State for submission status and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      // Perform field validations
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.service) validationErrors.push('Service is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.budget) validationErrors.push('Budget is required');
      if (!formData.date) validationErrors.push('Date is required');
      if (!formData.comment) validationErrors.push('Message is required');

      // If errors exist, throw as one error message
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle unsuccessful API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        service: '',
        address: '',
        budget: '',
        date: '',
        comment: ''
      });
    } 
    // Show error message
    catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Stop loading spinner
      setIsSubmitting(false);
    }
  };

  // Update form field state on input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="get-quote custom-form">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request A Quote'} subTitle={'Custom Woodwork Solutions'} linear sideHeading icon={<FaHammer />} />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service"><FaHammer /></label>
                  <select
                    className="select-box"
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Services</option>
                    <option value="custom-craft">Custom Craft</option>
                    <option value="carpentry-work">Carpentry Work</option>
                    <option value="furniture-repair">Furniture Repair</option>
                    <option value="cabinet-design">Cabinet Design</option>
                    <option value="flooring-installation">Flooring Installation</option>
                    <option value="deck-construction">Deck Construction</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              
              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaLocationDot /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Budget Input Field */}
              <div className="input-field">
                <label htmlFor="budget"><FaDollarSign /></label>
                <input
                  type="number"
                  name="budget"
                  className="box"
                  id="budget"
                  placeholder="Budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Date"
                  id="date"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Comment Text Area */}
            <div className="input-field text-area">
              <label htmlFor="comment"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="comment"
                className="box"
                id="comment"
                placeholder="Message"
                value={formData.comment}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;